import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { Collections } from '@graylog/enterprise-api';

import FiltersForQueryParams from 'components/common/EntityFilters/FiltersForQueryParams';
import type {
  SharedCollectionListItem,
  SortableCollectionAttributes,
  SharedCollectionsFilterableBy,
} from 'collections/types';
import type { Attribute, SearchParams } from 'stores/PaginationTypes';
import type { PaginatedResponse } from 'components/common/PaginatedEntityTable/useFetchEntities';
import { defaultOnError } from 'util/conditional/onError';

const INITIAL_DATA = {
  pagination: { total: 0 },
  list: [],
  attributes: [],
};

export const SHARED_COLLECTIONS_QUERY_KEY = 'shared_collections';

export const keyFn = (searchParams: SearchParams, filterBy: SharedCollectionsFilterableBy, id: string) => [
  SHARED_COLLECTIONS_QUERY_KEY,
  filterBy,
  id,
  searchParams,
];

export const fetchPaginatedSharedCollections = async (
  searchParams: SearchParams,
  filterBy: SharedCollectionsFilterableBy,
  id: string,
): Promise<PaginatedResponse<SharedCollectionListItem>> =>
  Collections.getSharedPaginated(
    id,
    filterBy,
    searchParams.sort.attributeId as SortableCollectionAttributes,
    searchParams.page,
    searchParams.pageSize,
    searchParams.query,
    FiltersForQueryParams(searchParams.filters),
    searchParams.sort.direction,
  ).then(({ elements, query, attributes, pagination: { count, total, page, per_page: perPage } }) => ({
    list: elements,
    attributes: attributes,
    pagination: {
      count,
      total,
      page,
      perPage,
      query,
    },
  }));

const useSharedCollections = (
  searchParams: SearchParams,
  id: string,
  filterBy: SharedCollectionsFilterableBy,
  { enabled } = { enabled: true },
): {
  data: {
    list: Readonly<Array<SharedCollectionListItem>>;
    pagination: { total: number };
    attributes: Array<Attribute>;
  };
  isLoading: boolean;
  refetch: () => void;
} => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: keyFn(searchParams, filterBy, id),
    queryFn: () =>
      defaultOnError(
        fetchPaginatedSharedCollections(searchParams, filterBy, id),
        'Loading Collections failed with status',
        'Could not load Collections',
      ),
    placeholderData: keepPreviousData,
    enabled,
  });

  return {
    data: data ?? INITIAL_DATA,
    isLoading,
    refetch,
  };
};

export default useSharedCollections;
